/**
 * F-148 §4.2 CSV parse feedback: one file error and/or the first 50 row errors, each with a
 * translated column name and message. Encoding failures carry the CSV UTF-8 guidance.
 */
import { useTranslation } from '@dukanti/shared';
import type { ReceivingCsvFileErrorCode, ReceivingCsvRowError } from '@dukanti/shared/admin';
import { csvColumnLabelKey, csvErrorMessageKey } from '../lib/error-messages';

export interface CsvImportErrorsProps {
  readonly fileError: ReceivingCsvFileErrorCode | null;
  readonly rowErrors: readonly ReceivingCsvRowError[];
}

const ENCODING_CODES: ReadonlySet<ReceivingCsvFileErrorCode> = new Set([
  'encoding_not_utf8',
  'utf16_encoding',
]);

export function CsvImportErrors({ fileError, rowErrors }: CsvImportErrorsProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div role="alert" data-testid="csv-import-errors" className="space-y-3 text-start text-sm">
      {fileError ? (
        <div
          className="border-destructive/40 bg-destructive/10 text-destructive rounded-lg border p-3"
          data-testid="csv-file-error"
          data-code={fileError}
        >
          <p className="font-medium">{t('admin.receiving.csv.fileError')}</p>
          <p>{t(csvErrorMessageKey(fileError))}</p>
          {ENCODING_CODES.has(fileError) ? (
            <p className="mt-1 text-xs" data-testid="csv-utf8-guidance">
              {t('admin.receiving.csv.utf8Guidance')}
            </p>
          ) : null}
        </div>
      ) : null}

      {rowErrors.length > 0 ? (
        <div className="border-border overflow-hidden rounded-lg border">
          <p className="bg-admin-table-header text-foreground px-3 py-2 text-xs font-semibold">
            {t('admin.receiving.csv.rowErrors')}
          </p>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-xs" data-testid="csv-row-errors">
              <thead className="sr-only">
                <tr>
                  <th scope="col">{t('admin.receiving.csv.row')}</th>
                  <th scope="col">{t('admin.receiving.csv.column')}</th>
                  <th scope="col">{t('admin.receiving.csv.problem')}</th>
                </tr>
              </thead>
              <tbody>
                {rowErrors.map((error, index) => {
                  const columnKey = csvColumnLabelKey(error.column);
                  return (
                    <tr
                      key={`${error.row}-${error.column ?? 'row'}-${error.code}-${index}`}
                      className="border-border border-t"
                      data-testid="csv-row-error"
                      data-row={error.row}
                      data-column={error.column ?? ''}
                      data-code={error.code}
                    >
                      <td className="text-muted-foreground whitespace-nowrap px-3 py-2 align-top">
                        {t('admin.receiving.csv.row')} <bdi dir="ltr">{error.row}</bdi>
                      </td>
                      <td className="text-muted-foreground whitespace-nowrap px-3 py-2 align-top">
                        {columnKey ? (
                          t(columnKey)
                        ) : error.column ? (
                          <bdi dir="ltr">{error.column}</bdi>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="text-foreground px-3 py-2 align-top">
                        {t(csvErrorMessageKey(error.code))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
